import React from 'react';
import { FC110 } from '../../../services/scApi';
import { dateToInput, inputToDate, fmt, fmtPct } from '../helpers';
import LookupField from '../../../components/LookupField';
import { AcceptanceFieldHint, pwc202f1AcceptanceManifest } from '../../../features/acceptance';

interface Tab1Props {
  formData: Partial<FC110>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FC110) => FC110[keyof FC110];
  setField: (key: keyof FC110, value: FC110[keyof FC110]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
  // Computed values
  salesAmt: number;
  purchaseAmt: number;
  expenseAmt: number;
  commIncome: number;
  commExpense: number;
  processingFee: number;
  grossProfit: number;
  grossProfitRate: number;
  documentKey?: string;
}

const Tab1BasicInfo: React.FC<Tab1Props> = ({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, resolveSingleName, getFieldError,
  salesAmt, purchaseAmt, expenseAmt,
  commIncome, commExpense, processingFee,
  grossProfit, grossProfitRate, documentKey,
}) => {
  const H = (fieldId: string) => <AcceptanceFieldHint manifest={pwc202f1AcceptanceManifest} fieldId={fieldId} documentKey={documentKey} />;
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{err}</span> : null;
  };
  return (
    <div className="tab1-layout">

      {/* === Left: Form Sections === */}
      <div className="form-sections">

        {/* Section 1: 業務人員資訊 */}
        <div className="form-section odd">
          <span className="section-tag">業務人員</span>
          <div className="form-grid">
            <label className="required">業務員代號{E('fc110_13')}{H('fc110_13')}</label>
            <div className="lookup-field">
              <LookupField lookupType="employee" style={{ width: 100 }} maxLength={8}
                disabled={isFieldDisabled('fc110_13')}
                value={getField('fc110_13') as string}
                onChange={v => setField('fc110_13', v)}
                onCodeConfirm={code => resolveSingleName('salesperson', 'employee', code)} contractFieldId="fc110_13"
              />
              <span className="lookup-name">{names.salesperson || '--'}</span>
            </div>

            <label>部門{H('fc110_10')}</label>
            <div>
              <input type="text" readOnly style={{ maxWidth: 140 }} value={getField('fc110_10') as string} data-contract-field="fc110_10" />
              <span style={{ marginLeft: 8, color: 'var(--text-tertiary)', fontSize: 12 }}>{names.department}</span>
            </div>

            <label data-contract-field="fc110_21">報價單號</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <LookupField lookupType="quotation" style={{ maxWidth: 140 }} maxLength={6}
                disabled={isFieldDisabled('fc110_21')}
                value={getField('fc110_21') as string}
                onChange={v => setField('fc110_21', v)}
                onCodeConfirm={code => resolveSingleName('quotation', 'quotation', code)}
              />
              <button className="btn btn-sm" disabled>報價單內容</button>
            </div>

            <label className="required">入單人員{H('fc110_18')}</label>
            <div className="lookup-field">
              <input type="text" readOnly style={{ width: 100 }} maxLength={8} value={getField('fc110_18') as string} data-contract-field="fc110_18" />
              <span className="lookup-name">{names.entryPerson || '--'}</span>
            </div>

            <label className="required">幣別{E('fc110_24')}{H('fc110_24')}</label>
            <div className="lookup-field">
              <LookupField lookupType="currency" style={{ width: 80 }}
                disabled={isFieldDisabled('fc110_24')}
                value={getField('fc110_24') as string}
                onChange={v => setField('fc110_24', v)}
                onCodeConfirm={code => resolveSingleName('currency', 'currency', code)} contractFieldId="fc110_24"
              />
              <span className="lookup-name">{names.currency || '--'}</span>
            </div>

            <label>匯率{H('fc110_25')}</label>
            <div>
              <input
                type="text" style={{ maxWidth: 120 }}
                readOnly
                value={getField('fc110_25') as string} data-contract-field="fc110_25"
              />
            </div>
          </div>
        </div>

        {/* Section 2: 客戶資訊 + 訂單屬性（合併） */}
        <div className="form-section even">
          <span className="section-tag">客戶 / 訂單</span>
          <div className="form-grid">
            {/* Row 1: 客戶代號 | 客戶單號 */}
            <label className="required">客戶代號{E('fc110_12')}{H('fc110_12')}</label>
            <div className="lookup-field">
              <LookupField lookupType="customer" style={{ width: 120 }} maxLength={12}
                disabled={isFieldDisabled('fc110_12')}
                value={getField('fc110_12') as string}
                onChange={v => setField('fc110_12', v)}
                onCodeConfirm={code => resolveSingleName('customer', 'customer', code)} contractFieldId="fc110_12"
              />
              <span className="lookup-name">{names.customer || '--'}</span>
            </div>

            <label data-contract-field="fc110_14">客戶單號</label>
            <div style={{ gridColumn: 'span 3' }}>
              <textarea
                disabled={!isEditing}
                value={getField('fc110_14') as string}
                onChange={e => setField('fc110_14', e.target.value)}
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            {/* Row 2: 成衣買主 | 中間買主 */}
            <label>成衣買主{H('fc110_17')}</label>
            <div className="lookup-field">
              <LookupField lookupType="customer" style={{ width: 100 }} maxLength={12}
                disabled={isFieldDisabled('fc110_17')}
                value={getField('fc110_17') as string}
                onChange={v => setField('fc110_17', v)}
                onCodeConfirm={code => resolveSingleName('garmentBuyer', 'customer', code)} contractFieldId="fc110_17"
              />
              <span className="lookup-name">{names.garmentBuyer || '--'}</span>
            </div>

            <label>中間買主{H('fc110_16')}</label>
            <div className="lookup-field">
              <LookupField lookupType="customer" style={{ width: 100 }} maxLength={12}
                disabled={isFieldDisabled('fc110_16')}
                value={getField('fc110_16') as string}
                onChange={v => setField('fc110_16', v)}
                onCodeConfirm={code => resolveSingleName('middleman', 'customer', code)} contractFieldId="fc110_16"
              />
              <span className="lookup-name">{names.middleman || '--'}</span>
            </div>

            <label className="required">業績歸屬客戶{H('fc110_59')}</label>
            <div className="lookup-field">
              <LookupField lookupType="customer" style={{ width: 100 }}
                disabled={isFieldDisabled('fc110_59')}
                value={getField('fc110_59') as string}
                onChange={v => setField('fc110_59', v)}
                onCodeConfirm={code => resolveSingleName('perfCustomer', 'customer', code)} contractFieldId="fc110_59"
              />
              <span className="lookup-name">{names.perfCustomer || '--'}</span>
            </div>

            {/* Row 3: 銷售樣單號 | 品牌 | 訂單類別 */}
            <label>銷售樣單號{H('fc110_26')}</label>
            <div className="lookup-field">
              <LookupField lookupType="salesSample" style={{ width: 80 }} maxLength={6}
                disabled={isFieldDisabled('fc110_26')}
                value={getField('fc110_26') as string}
                onChange={v => setField('fc110_26', v)}
                onCodeConfirm={code => resolveSingleName('salesSample', 'salesSample', code)} contractFieldId="fc110_26"
              />
              <span className="lookup-name">{names.salesSample || '--'}</span>
            </div>

            <label data-contract-field="fc110_60">品牌</label>
            <div>
              <input
                type="text"
                disabled={isFieldDisabled('fc110_60')}
                value={getField('fc110_60') as string}
                onChange={e => setField('fc110_60', e.target.value)}
              />
            </div>

            <label>訂單類別</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio" name="orderType" value="1"
                  disabled={!isEditing}
                  checked={(formData.fc110_58 || 1) === 1}
                  onChange={() => setField('fc110_58', 1)}
                />
                大貨
              </label>
              <label>
                <input
                  type="radio" name="orderType" value="2"
                  disabled={!isEditing}
                  checked={formData.fc110_58 === 2}
                  onChange={() => setField('fc110_58', 2)}
                />
                樣品
              </label>
            </div>
          </div>
        </div>

        {/* Section 3: 送貨與付款 */}
        <div className="form-section odd">
          <span className="section-tag">送貨付款</span>
          <div className="form-grid">
            <label data-contract-field="fc110_20">送貨地點</label>
            <div style={{ gridColumn: 'span 5' }}>
              <input
                type="text"
                disabled={isFieldDisabled('fc110_20')}
                value={getField('fc110_20') as string}
                onChange={e => setField('fc110_20', e.target.value)}
              />
            </div>

            <label className="required">付款條件{H('fc110_22')}</label>
            <div className="lookup-field">
              <LookupField lookupType="paymentTerms" style={{ width: 80 }}
                disabled={isFieldDisabled('fc110_22')}
                value={getField('fc110_22') as string}
                onChange={v => setField('fc110_22', v)}
                onCodeConfirm={code => resolveSingleName('paymentTerms', 'paymentTerms', code)} contractFieldId="fc110_22"
              />
              <span className="lookup-name">{names.paymentTerms || '--'}</span>
            </div>

            <label>L/C收到日{H('fc110_15')}</label>
            <div>
              <input
                type="date"
                disabled={isFieldDisabled('fc110_15')}
                value={dateToInput(getField('fc110_15') as string)}
                onChange={e => setField('fc110_15', inputToDate(e.target.value) as string)} data-contract-field="fc110_15"
              />
            </div>
          </div>
        </div>

      </div>

      {/* Right: Summary Card (sticky sidebar) */}
      <div className="summary-card">
        <div className="summary-title">金額匯總</div>
        <div className="summary-grid">
          <div className="summary-item positive">
            <span className="label">銷售 (+)</span>
            <span className="value">{fmt(salesAmt)}</span>
          </div>
          <div className="summary-item negative">
            <span className="label">採購 (-)</span>
            <span className="value">{fmt(purchaseAmt)}</span>
          </div>
          <div className="summary-item negative">
            <span className="label">費用 (-)</span>
            <span className="value">{fmt(expenseAmt)}</span>
          </div>
          <div className="summary-item positive">
            <span className="label">佣金收入 (+)</span>
            <span className="value">{fmt(commIncome)}</span>
          </div>
          <div className="summary-item negative">
            <span className="label">佣金支出 (-)</span>
            <span className="value">{fmt(commExpense)}</span>
          </div>
          <div className="summary-item negative">
            <span className="label">工繳 (-)</span>
            <span className="value">{fmt(processingFee)}</span>
          </div>

          <div className="summary-divider" />

          <div className="summary-item total">
            <span className="label">總毛利</span>
            <span className="value">{fmt(grossProfit)}</span>
          </div>
          <div className="summary-item total">
            <span className="label">總毛利率</span>
            <span className="value">{fmtPct(grossProfitRate)} %</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default React.memo(Tab1BasicInfo);
